/**
 * Pure helpers for the in-memory chat-message cache.
 *
 * Extracted out of useGameSocket.ts so they can be unit-tested without
 * pulling in react-native (the hook imports AppState + Platform, which
 * the node test runner cannot parse).
 */

import type { ChatMessageResponse, MessageReactionResponse } from '@warcarp/api-types'

/**
 * Append an incoming WS chat message to the cached chat list.
 *
 * - When the cache is empty (no previous fetch), seed it with just the
 *   incoming message rather than returning undefined — the chat screen
 *   would otherwise stay on its empty state until the next focus
 *   refetch.
 * - Dedupe by message id so a reconnect-echo or a self-post optimism
 *   collision doesn't double-render the same row.
 */
export function appendChatMessage(
  old: ChatMessageResponse[] | undefined,
  incoming: ChatMessageResponse,
): ChatMessageResponse[] {
  if (!old) return [incoming]
  if (old.some((m) => m.id === incoming.id)) return old
  return [...old, incoming]
}

/** Replace one message's reactions with the server's canonical toggle result. */
export function updateChatMessageReactions(
  old: ChatMessageResponse[] | undefined,
  messageId: string,
  reactions: MessageReactionResponse[],
): ChatMessageResponse[] | undefined {
  if (!old) return old
  let changed = false
  const next = old.map((message) => {
    if (message.id !== messageId) return message
    changed = true
    return { ...message, reactions }
  })
  return changed ? next : old
}
